import { Component, inject } from '@angular/core';
import { SavedPin } from '@models/teranet';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select } from '@ngxs/store';
import { SalePinActions, SalePinState } from '@state/sale_pins';
import { combineLatest, lastValueFrom, Observable } from 'rxjs';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { MatterState, TITLE_SEARCH_MATTER_STATE } from '@models/matter';
import { MatterActions, MatterDispatch } from '@state/matter';
import { NavigationService } from 'src/app/shared/services/nav/nav.service';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { ICON } from '@core/util/icon';
import { TestHooks } from './teranet.hooks';
import { TeranetService } from '@services/teranet/teranet.service';
import LogRocket from 'logrocket';

@UntilDestroy()
@Component({
	selector: 'app-teranet',
	templateUrl: './teranet.component.html',
	styleUrls: ['./teranet.component.scss'],
	standalone: false,
})
export class TeranetComponent {
	testHooks = TestHooks;
	@SelectSnapshot(SelectedMatter.id) matterId: string;
	@Select(SalePinState.getSelected) selectedPin$: Observable<SavedPin>;
	@Select(SalePinState.getPrimary) primaryPin$: Observable<SavedPin>;
	@Select(SelectedMatter.matterState) matterState$: Observable<MatterState>;

	teranetService = inject(TeranetService);

	public showEmptyState = true;
	public ICON = ICON;

	constructor(
		private routeService: NavigationService,
		private matterDispatch: MatterDispatch,
	) {
		this.updateBalance();

		combineLatest([this.selectedPin$, this.primaryPin$])
			.pipe(
				untilDestroyed(this),
				map(([selectedPin, primaryPin]) => {
					if (!selectedPin && primaryPin?.pin) {
						this.selectPin(primaryPin.pin);
					}
				}),
			)
			.subscribe();
		this.matterState$
			.pipe(
				untilDestroyed(this),
				distinctUntilChanged((a, b) => {
					return a?.titleSearch === b?.titleSearch;
				}),
			)
			.subscribe((state) => {
				if (state?.titleSearch) {
					switch (state.titleSearch) {
						case TITLE_SEARCH_MATTER_STATE.STARTING_PROCESS:
							this.showEmptyState = true;
							break;
						case TITLE_SEARCH_MATTER_STATE.SEARCH:
							this.showEmptyState = false;
							this.routeService.goToMatter_TitleSearch_Stepper_Search(
								this.matterId,
							);
							break;
						case TITLE_SEARCH_MATTER_STATE.SYNC:
							this.showEmptyState = false;
							this.routeService.goToMatter_TitleSearch_Stepper_Sync(
								this.matterId,
							);
							break;
						case TITLE_SEARCH_MATTER_STATE.ADJACENT_ANALYSIS:
							this.showEmptyState = false;
							this.routeService.goToMatter_TitleSearch_Stepper_AdjacentAnalysis(
								this.matterId,
							);
							break;
						case TITLE_SEARCH_MATTER_STATE.INSTRUMENTS:
							this.showEmptyState = false;
							this.routeService.goToMatter_TitleSearch_Stepper_Instruments(
								this.matterId,
							);
							break;
						case TITLE_SEARCH_MATTER_STATE.ACTIONS:
							this.showEmptyState = false;
							this.routeService.goToMatter_TitleSearch_Stepper_Requisitions(
								this.matterId,
							);
							break;
						case TITLE_SEARCH_MATTER_STATE.WRITS:
							this.showEmptyState = false;
							this.routeService.goToMatter_TitleSearch_Stepper_Writs(
								this.matterId,
							);
							break;
						case TITLE_SEARCH_MATTER_STATE.COMPLETE:
							this.showEmptyState = false;
							this.routeService.goToMatter_TitleSearch(this.matterId);
					}
				}
			});
	}

	async updateBalance() {
		if (!this.teranetService?.teranetCredentials()?.password) {
			return;
		}

		try {
			await lastValueFrom(this.teranetService.getAndUpdateBalance());
		} catch (err) {
			LogRocket.error('Error updating Teranet balance:', err);
		}
	}

	@Dispatch()
	setTitleState(saleId: string): MatterActions.SetSaleStateTitleSearch {
		return new MatterActions.SetSaleStateTitleSearch({
			matterId: saleId,
			titleSearchState: TITLE_SEARCH_MATTER_STATE.SEARCH,
		});
	}

	@Dispatch()
	selectPin(pin: string): SalePinActions.SelectPin {
		return new SalePinActions.SelectPin(pin);
	}

	startSearch() {
		this.matterDispatch.SetTitleState(
			this.matterId,
			TITLE_SEARCH_MATTER_STATE.SEARCH,
		);
	}
}
